import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowUpRight,
  Clock,
  Compass,
  FolderOpen,
  HelpCircle,
  Loader2,
  LogOut,
  User,
  Zap,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import CachedAvatar from '../../components/CachedAvatar'
import SubscriptionModal from '../subscription/SubscriptionModal'
import PaymentPackModal from '../subscription/PaymentPackModal'
import type { HomeTab } from './types'

interface LeftSidebarProps {
  activeTab: HomeTab
}

export default function LeftSidebar({ activeTab }: LeftSidebarProps) {
  const navigate = useNavigate()
  const { user, login, logout, isLoading } = useAuth()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false)
  const [isPaymentPackModalOpen, setIsPaymentPackModalOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isUserMenuOpen) {
      return undefined
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isUserMenuOpen])

  return (
    <>
      <div className="flex h-screen flex-col bg-[#F4F4F5]">
        {/* Logo */}
        <div className="p-6">
          <div className="flex items-center gap-3">
            <svg
              className="h-10 w-10"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect x="4" y="4" width="40" height="40" rx="12" fill="#0B0B0B" />
              <path
                d="M31 14.5C28.2 11.7 19.8 11.7 17 14.5C14.2 17.3 14.2 20.8 24 22.6C33.8 24.4 33.8 29.7 31 32.5C28.2 35.3 19.8 35.3 17 32.5"
                stroke="#FFFFFF"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-400">
                Sequence
              </p>
              <p className="text-lg font-semibold text-gray-900">Studio</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <nav className="flex-1 p-4">
          <div className="space-y-2">
            {/* Explore Tab */}
            <button
              onClick={() => navigate('/')}
              className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'explore'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Compass className="h-5 w-5" />
              <span>Explore</span>
            </button>

            {/* Projects Tab */}
            <button
              onClick={() => navigate('/projects')}
              className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'projects'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <FolderOpen className="h-5 w-5" />
              <span>Projects</span>
            </button>
          </div>
        </nav>

        {/* User Profile */}
        <div className="p-4">
          {user ? (
            <div className="relative" ref={userMenuRef}>
              {isUserMenuOpen && (
                <div className="absolute bottom-full left-0 z-50 mb-4 w-64 rounded-2xl border border-gray-100 bg-white p-3 shadow-2xl">
                  <div className="flex items-center gap-2.5">
                    <CachedAvatar
                      avatarUrl={user.avatar}
                      userName={user.name}
                      userIdentifier={user.uid}
                      size="md"
                    />
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate text-sm font-semibold text-gray-900">{user.name}</p>
                      <p className="truncate text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-semibold text-gray-900">{user.credits}</span>
                      <span className="text-xs text-gray-500">credits left</span>
                      <Clock className="h-3.5 w-3.5 text-gray-400" />
                    </div>
                    <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
                      {user.plan}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-sky-50 px-2.5 py-1.5 text-xs font-medium text-sky-700 transition hover:bg-sky-100"
                  >
                    How credits work
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsSubscriptionModalOpen(true)
                      setIsUserMenuOpen(false)
                    }}
                    className="mt-3 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 py-2.5 text-sm font-semibold text-white shadow-md transition hover:shadow-lg"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Upgrade plan
                  </button>

                  <div className="my-3 border-t border-gray-100" />

                  <div className="space-y-0.5">
                    <button
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                      Help
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        logout()
                        setIsUserMenuOpen(false)
                      }}
                      className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      Log out
                    </button>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => setIsUserMenuOpen((open) => !open)}
                className="flex w-full cursor-pointer items-center gap-3 rounded-lg p-3 transition hover:bg-gray-50"
                aria-haspopup="dialog"
                aria-expanded={isUserMenuOpen}
              >
                <CachedAvatar
                  avatarUrl={user.avatar}
                  userName={user.name}
                  userIdentifier={user.uid}
                  size="md"
                />
                <div className="flex-1 overflow-hidden text-left">
                  <p className="truncate text-sm font-medium text-gray-900">{user.name}</p>
                  <p className="truncate text-xs text-gray-500">{user.email}</p>
                </div>
              </button>
            </div>
          ) : (
            <button
              onClick={async () => {
                try {
                  setIsLoggingIn(true)
                  await login()
                } catch (error: any) {
                  alert('Login failed: ' + (error.message || error))
                } finally {
                  setIsLoggingIn(false)
                }
              }}
              disabled={isLoggingIn || isLoading}
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#1C1C1E] py-3 text-sm font-medium text-white shadow-sm transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isLoggingIn || isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <User className="h-5 w-5" />
                  <span>Log In</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        onSwitchToPaymentPack={() => {
          setIsSubscriptionModalOpen(false)
          setIsPaymentPackModalOpen(true)
        }}
      />
      <PaymentPackModal
        isOpen={isPaymentPackModalOpen}
        onClose={() => setIsPaymentPackModalOpen(false)}
      />
    </>
  )
}
